import { useEffect, useRef, useState } from 'react'
import { Bot, RefreshCw, Save, Sparkles } from 'lucide-react'
import type { BrNarrative, BrSummary } from '../../hooks/useBusinessReview'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function localStorageKey(periodStart: string, periodEnd: string): string {
  return `br-management-note-${periodStart}-${periodEnd}`
}

function loadNote(periodStart: string, periodEnd: string): string {
  try {
    return localStorage.getItem(localStorageKey(periodStart, periodEnd)) ?? ''
  } catch {
    return ''
  }
}

function saveNote(periodStart: string, periodEnd: string, content: string): void {
  try {
    localStorage.setItem(localStorageKey(periodStart, periodEnd), content)
  } catch {
    // localStorage non disponibile — fallback silenzioso
  }
}

// ---------------------------------------------------------------------------
// Exported pure function — testabile in isolamento
// ---------------------------------------------------------------------------

export function narrativeStatusLabel(
  status: BrSummary['narrative_status'],
  narrative: BrNarrative | null,
): string {
  if (status === 'ok' && narrative) return 'Generata'
  if (status === 'unavailable') return 'Non disponibile'
  if (status === 'disabled') return 'Disabilitata'
  return 'Non disponibile'
}

// ---------------------------------------------------------------------------
// Draft AI tab
// ---------------------------------------------------------------------------

interface DraftTabProps {
  narrative: BrNarrative | null
  narrativeStatus: BrSummary['narrative_status']
  onRefresh: () => void
  isRefreshing: boolean
}

function DraftTab({ narrative, narrativeStatus, onRefresh, isRefreshing }: DraftTabProps) {
  return (
    <div data-testid="draft-ai-tab" className="space-y-3">
      {/* Status + azioni */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-3.5 h-3.5 text-indigo-400" />
          <span className="text-xs text-[--km-text-secondary]">
            Claude Sonnet —{' '}
            <span
              data-testid="narrative-status-label"
              className={
                narrativeStatus === 'ok'
                  ? 'text-emerald-400'
                  : narrativeStatus === 'disabled'
                    ? 'text-[--km-text-disabled]'
                    : 'text-amber-400'
              }
            >
              {narrativeStatusLabel(narrativeStatus, narrative)}
            </span>
          </span>
          {narrative?.generated_at && (
            <span className="text-[10px] text-[--km-text-disabled]">
              · {new Date(narrative.generated_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="refresh-narrative-btn"
          onClick={onRefresh}
          disabled={isRefreshing || narrativeStatus === 'disabled'}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[var(--km-card-border)] text-[10px] text-[--km-text-secondary] hover:text-[--km-text-primary] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
          Genera draft
        </button>
      </div>

      {/* Contenuto */}
      {narrative?.content ? (
        <div
          data-testid="narrative-content"
          className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-bg-surface)]/50 px-4 py-3 text-xs text-[--km-text-primary] leading-relaxed whitespace-pre-wrap"
        >
          {narrative.content}
        </div>
      ) : (
        <div
          data-testid="narrative-empty"
          className="rounded-xl border border-dashed border-[var(--km-card-border)] px-4 py-6 text-center"
        >
          {narrativeStatus === 'disabled' ? (
            <p className="text-xs text-[--km-text-disabled]">
              La narrativa AI è disabilitata per questo template.
            </p>
          ) : (
            <p className="text-xs text-[--km-text-secondary]">
              Nessun draft disponibile — clicca "Genera draft" per generare la narrativa AI.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Management Note tab
// ---------------------------------------------------------------------------

const AUTOSAVE_DELAY_MS = 3000 // 3s per test; spec dice 30s in produzione

interface ManagementNoteTabProps {
  periodStart: string
  periodEnd: string
}

function ManagementNoteTab({ periodStart, periodEnd }: ManagementNoteTabProps) {
  const [note, setNote] = useState(() => loadNote(periodStart, periodEnd))
  const [saved, setSaved] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-save debounced
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      saveNote(periodStart, periodEnd, note)
      setSaved(true)
    }, AUTOSAVE_DELAY_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [note, periodStart, periodEnd])

  function handleSaveNow() {
    if (timerRef.current) clearTimeout(timerRef.current)
    saveNote(periodStart, periodEnd, note)
    setSaved(true)
  }

  return (
    <div data-testid="management-note-tab" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[--km-text-secondary]">
          Nota ufficiale — appare nell'export board. Salvataggio locale automatico.
        </p>
        <div className="flex items-center gap-2">
          {saved && (
            <span data-testid="autosave-indicator" className="text-[10px] text-emerald-400">
              Salvato
            </span>
          )}
          <button
            type="button"
            data-testid="save-note-btn"
            onClick={handleSaveNow}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[var(--km-card-border)] text-[10px] text-[--km-text-secondary] hover:text-[--km-text-primary] transition-colors"
          >
            <Save className="w-3 h-3" />
            Salva
          </button>
        </div>
      </div>

      <textarea
        data-testid="management-note-textarea"
        value={note}
        onChange={e => {
          setNote(e.target.value)
          setSaved(false)
        }}
        placeholder="Inserisci la nota di management per questa review..."
        rows={6}
        className="w-full rounded-xl border border-[var(--km-card-border)] bg-[var(--km-bg-surface)]/50 px-4 py-3 text-xs text-[--km-text-primary] placeholder:text-[--km-text-disabled] focus:outline-none focus:border-indigo-500/50 resize-none leading-relaxed"
      />

      {!note && (
        <p className="text-[10px] text-[--km-text-disabled] italic">
          Se presente, questa nota sostituisce il draft AI nell'export. Se assente, il draft AI è usato con watermark "Bozza AI — non approvata".
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// InsightBox
// ---------------------------------------------------------------------------

interface InsightBoxProps {
  summary: BrSummary
  periodStart: string
  periodEnd: string
  onRefreshNarrative: () => void
  isRefreshing?: boolean
}

export function InsightBox({
  summary,
  periodStart,
  periodEnd,
  onRefreshNarrative,
  isRefreshing = false,
}: InsightBoxProps) {
  const [activeTab, setActiveTab] = useState<'ai' | 'management'>('ai')

  return (
    <div
      data-testid="insight-box"
      className="rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] shadow-[var(--km-shadow-md)] overflow-hidden"
    >
      {/* Header con tab */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--km-card-border)]">
        <div className="flex items-center gap-1 rounded-xl border border-[var(--km-card-border)] bg-[var(--km-bg-surface)]/50 p-1">
          <button
            type="button"
            data-testid="tab-ai"
            onClick={() => setActiveTab('ai')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab === 'ai'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-[--km-text-secondary] hover:text-[--km-text-primary]'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Draft AI
          </button>
          <button
            type="button"
            data-testid="tab-management"
            onClick={() => setActiveTab('management')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab === 'management'
                ? 'bg-indigo-500/20 text-indigo-300'
                : 'text-[--km-text-secondary] hover:text-[--km-text-primary]'
            }`}
          >
            <Save className="w-3.5 h-3.5" />
            Nota management
          </button>
        </div>

        {activeTab === 'ai' && (
          <span className="text-[10px] text-[--km-text-disabled]">
            Generata da Claude Sonnet · tono per audience template
          </span>
        )}
      </div>

      {/* Tab content */}
      <div className="px-5 py-4">
        {activeTab === 'ai' ? (
          <DraftTab
            narrative={summary.ai_narrative}
            narrativeStatus={summary.narrative_status}
            onRefresh={onRefreshNarrative}
            isRefreshing={isRefreshing}
          />
        ) : (
          <ManagementNoteTab periodStart={periodStart} periodEnd={periodEnd} />
        )}
      </div>
    </div>
  )
}
